import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Collapse,
  Typography,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  School as AcademyIcon,
  School as LearningIcon,
  FitnessCenter as TrainingIcon,
  Assessment as AssessmentIcon,
  EmojiEvents as RankingIcon,
  Group as PlayersIcon,
  People as CoachesIcon,
  Security as PermissionsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  WorkspacePremium as CertificateIcon,
  Compare as CompareIcon,
  Description as ReportsIcon,
  AutoAwesome as RecommendationsIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import EncycdataLogo from '@components/layout/EncycdataLogo';
import SidebarAccountFooter from '@components/layout/SidebarAccountFooter';

const DRAWER_WIDTH = 280;

/** Coach-only tools: administration, student evaluation, compare */
const COACH_THEME = {
  drawerBg: '#15202b',
  sectionBg: '#1a2836',
  hoverBg: '#243447',
  activeBg: '#2d4a6f',
  text: '#b0bec5',
  accent: '#42a5f5',
  headerBg: 'rgba(21, 101, 192, 0.18)',
  headerBorder: '#1565c0',
  toolbarBg: '#1565c0',
};

/** Coach-as-player: mirrors player portal (purple toolbar accent, orange active) */
const PLAYER_THEME = {
  sectionBg: '#2c3e50',
  hoverBg: '#34495e',
  activeBg: '#34495e',
  text: '#ecf0f1',
  accent: '#ff9800',
  headerBg: 'rgba(160, 90, 255, 0.15)',
  headerBorder: '#A05AFF',
};

interface CoachLayoutProps {
  children?: React.ReactNode;
}

type NavTheme = typeof COACH_THEME | typeof PLAYER_THEME;

const CoachLayout: React.FC<CoachLayoutProps> = ({ children }) => {
  const { logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState<string[]>(['academy-management']);
  const isCoursesView =
    location.pathname.startsWith('/coach/learning/courses') || location.pathname === '/coach/learning';
  const isRoadmapView = location.pathname.startsWith('/coach/learning/roadmap');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleToggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]
    );
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const itemSx = (path: string, theme: NavTheme, selected?: boolean) => {
    const active = selected ?? isActive(path);
    return {
      color: theme.text,
      bgcolor: active ? theme.activeBg : 'transparent',
      '&:hover': { bgcolor: theme.hoverBg },
      '&.Mui-selected': {
        bgcolor: theme.activeBg,
        color: theme.accent,
        '&:hover': { bgcolor: theme.activeBg },
      },
    };
  };

  const iconColor = (path: string, theme: NavTheme, selected?: boolean) =>
    (selected ?? isActive(path)) ? theme.accent : theme.text;

  const textProps = (path: string, theme: NavTheme, selected?: boolean) => ({
    fontSize: '0.9rem',
    fontWeight: (selected ?? isActive(path)) ? 700 : 600,
    color: (selected ?? isActive(path)) ? theme.accent : theme.text,
  });

  const isLearnAndRankActive =
    isActive('/coach/ranking-overview') ||
    isActive('/coach/ranking-evaluation') ||
    isActive('/coach/my-rankings');

  const isTrainingEvaluationActive =
    isActive('/coach/performance') || isActive('/coach/my-trainings');

  const isAdminChildActive = (path: string) => isActive(path);

  const SectionHeader = ({ label, theme, subtitle }: { label: string; theme: NavTheme; subtitle?: string }) => (
    <Box
      sx={{
        px: 2,
        py: 1,
        mx: 1.5,
        mt: 1.5,
        mb: 0.5,
        bgcolor: theme.headerBg,
        borderLeft: `4px solid ${theme.headerBorder}`,
        borderRadius: '0 6px 6px 0',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontWeight: 800,
          letterSpacing: '0.08em',
          color: theme.accent,
          textTransform: 'uppercase',
          lineHeight: 1.3,
        }}
      >
        {label}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: theme.text, opacity: 0.85, fontSize: '0.68rem' }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  const drawer = (
    <Box sx={{ bgcolor: COACH_THEME.drawerBg, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ bgcolor: COACH_THEME.toolbarBg, minHeight: '48px !important', gap: 1 }}>
        <EncycdataLogo height={28} lightBackground sx={{ mr: 1 }} />
        <Box>
          <Typography variant="h6" noWrap sx={{ color: '#ffffff', fontWeight: 700, lineHeight: 1.2 }}>
            Coach Portal
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.65rem' }}>
            Coaching &amp; administration
          </Typography>
        </Box>
      </Toolbar>

      <List sx={{ flexGrow: 1, pt: 0.5, pb: 0 }}>
        {/* Dashboard & profile — neutral top links */}
        <ListItem disablePadding>
          <ListItemButton
            selected={isActive('/coach/dashboard')}
            onClick={() => navigate('/coach/dashboard')}
            sx={itemSx('/coach/dashboard', COACH_THEME)}
          >
            <ListItemIcon sx={{ color: iconColor('/coach/dashboard', COACH_THEME), minWidth: 56 }}>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary="Dashboard" primaryTypographyProps={textProps('/coach/dashboard', COACH_THEME)} />
          </ListItemButton>
        </ListItem>

        {/* ── COACH FEATURES (blue theme) ── */}
        <SectionHeader label="Coach Features" theme={COACH_THEME} subtitle="Academy tools — more coach features later" />

        <Box sx={{ bgcolor: COACH_THEME.sectionBg, mx: 1, borderRadius: 2, py: 0.5, mb: 0.5 }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleToggleSection('academy-management')}
              sx={{ color: COACH_THEME.text, '&:hover': { bgcolor: COACH_THEME.hoverBg } }}
            >
              <ListItemIcon sx={{ color: COACH_THEME.accent, minWidth: 56 }}>
                <AcademyIcon />
              </ListItemIcon>
              <ListItemText
                primary="Administration"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600, color: COACH_THEME.text }}
              />
              {expandedSections.includes('academy-management') ? (
                <ExpandLessIcon sx={{ color: COACH_THEME.text }} />
              ) : (
                <ExpandMoreIcon sx={{ color: COACH_THEME.text }} />
              )}
            </ListItemButton>
          </ListItem>
          <Collapse in={expandedSections.includes('academy-management')} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {[
                { path: '/coach/academy-management/academies', label: 'Academy', icon: AcademyIcon, match: () => isActive('/coach/academy-management') || isActive('/coach/academy-management/academies') },
                { path: '/coach/academy-management/players', label: 'Players', icon: PlayersIcon },
                { path: '/coach/academy-management/coaches', label: 'Coaches', icon: CoachesIcon },
                { path: '/coach/academy-management/permissions', label: 'Permissions', icon: PermissionsIcon },
                { path: '/coach/junior-coaches', label: 'Junior Coaches', icon: CoachesIcon },
              ].map(({ path, label, icon: Icon, match }) => {
                const active = match ? match() : isAdminChildActive(path);
                return (
                  <ListItemButton
                    key={path}
                    sx={{
                      pl: 4,
                      color: COACH_THEME.text,
                      bgcolor: active ? COACH_THEME.activeBg : 'transparent',
                      '&:hover': { bgcolor: COACH_THEME.hoverBg },
                    }}
                    onClick={() => navigate(path)}
                  >
                    <ListItemIcon sx={{ color: active ? COACH_THEME.accent : COACH_THEME.text, minWidth: 56 }}>
                      <Icon />
                    </ListItemIcon>
                    <ListItemText
                      primary={label}
                      primaryTypographyProps={{ fontSize: '0.9rem', color: active ? COACH_THEME.accent : COACH_THEME.text, fontWeight: active ? 700 : 600 }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Collapse>

          <ListItem disablePadding>
            <ListItemButton
              selected={isActive('/coach/player-training')}
              onClick={() => navigate('/coach/player-training')}
              sx={itemSx('/coach/player-training', COACH_THEME)}
            >
              <ListItemIcon sx={{ color: iconColor('/coach/player-training', COACH_THEME), minWidth: 56 }}>
                <TrainingIcon />
              </ListItemIcon>
              <ListItemText primary="Player Training" primaryTypographyProps={textProps('/coach/player-training', COACH_THEME)} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={isActive('/coach/player-ranking')}
              onClick={() => navigate('/coach/player-ranking')}
              sx={itemSx('/coach/player-ranking', COACH_THEME)}
            >
              <ListItemIcon sx={{ color: iconColor('/coach/player-ranking', COACH_THEME), minWidth: 56 }}>
                <RankingIcon />
              </ListItemIcon>
              <ListItemText primary="Player Ranking" primaryTypographyProps={textProps('/coach/player-ranking', COACH_THEME)} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={isActive('/coach/compare-students')}
              onClick={() => navigate('/coach/compare-students')}
              sx={itemSx('/coach/compare-students', COACH_THEME)}
            >
              <ListItemIcon sx={{ color: iconColor('/coach/compare-students', COACH_THEME), minWidth: 56 }}>
                <CompareIcon />
              </ListItemIcon>
              <ListItemText primary="Compare Students" primaryTypographyProps={textProps('/coach/compare-students', COACH_THEME)} />
            </ListItemButton>
          </ListItem>
        </Box>

        {/* ── MY LEARNING (player theme — orange / purple) ── */}
        <SectionHeader label="My Learning" theme={PLAYER_THEME} subtitle="You as a player — same as player portal" />

        <Box sx={{ bgcolor: PLAYER_THEME.sectionBg, mx: 1, borderRadius: 2, py: 0.5, mb: 0.5 }}>
          <ListItem disablePadding>
            <ListItemButton
              selected={isActive('/coach/learning')}
              onClick={() => navigate('/coach/learning/courses')}
              sx={itemSx('/coach/learning', PLAYER_THEME)}
            >
              <ListItemIcon sx={{ color: iconColor('/coach/learning', PLAYER_THEME), minWidth: 56 }}>
                <LearningIcon />
              </ListItemIcon>
              <ListItemText primary="Learning" primaryTypographyProps={textProps('/coach/learning', PLAYER_THEME)} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={isCoursesView}
              onClick={() => navigate('/coach/learning/courses')}
              sx={{
                pl: 7,
                ...itemSx('/coach/learning/courses', PLAYER_THEME, isCoursesView),
              }}
            >
              <ListItemIcon sx={{ color: iconColor('/coach/learning/courses', PLAYER_THEME, isCoursesView), minWidth: 36 }}>
                <LearningIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Courses" primaryTypographyProps={{ ...textProps('/coach/learning/courses', PLAYER_THEME, isCoursesView), fontSize: '0.85rem' }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={isRoadmapView}
              onClick={() => navigate('/coach/learning/roadmap')}
              sx={{
                pl: 7,
                ...itemSx('/coach/learning/roadmap', PLAYER_THEME, isRoadmapView),
              }}
            >
              <ListItemIcon sx={{ color: iconColor('/coach/learning/roadmap', PLAYER_THEME, isRoadmapView), minWidth: 36 }}>
                <TimelineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Roadmap" primaryTypographyProps={{ ...textProps('/coach/learning/roadmap', PLAYER_THEME, isRoadmapView), fontSize: '0.85rem' }} />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              selected={isLearnAndRankActive}
              onClick={() => navigate('/coach/ranking-overview')}
              sx={itemSx('/coach/ranking-overview', PLAYER_THEME, isLearnAndRankActive)}
            >
              <ListItemIcon sx={{ color: iconColor('/coach/ranking-overview', PLAYER_THEME, isLearnAndRankActive), minWidth: 56 }}>
                <RankingIcon />
              </ListItemIcon>
              <ListItemText primary="Learn and Rank" primaryTypographyProps={textProps('/coach/ranking-overview', PLAYER_THEME, isLearnAndRankActive)} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={isTrainingEvaluationActive}
              onClick={() => navigate('/coach/performance/Badminton/skill-assessment-beginner-self')}
              sx={itemSx('/coach/performance', PLAYER_THEME, isTrainingEvaluationActive)}
            >
              <ListItemIcon sx={{ color: iconColor('/coach/performance', PLAYER_THEME, isTrainingEvaluationActive), minWidth: 56 }}>
                <AssessmentIcon />
              </ListItemIcon>
              <ListItemText primary="Training Evaluation" primaryTypographyProps={textProps('/coach/performance', PLAYER_THEME, isTrainingEvaluationActive)} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={isActive('/coach/reports')}
              onClick={() => navigate('/coach/reports')}
              sx={itemSx('/coach/reports', PLAYER_THEME)}
            >
              <ListItemIcon sx={{ color: iconColor('/coach/reports', PLAYER_THEME), minWidth: 56 }}>
                <ReportsIcon />
              </ListItemIcon>
              <ListItemText primary="Reports" primaryTypographyProps={textProps('/coach/reports', PLAYER_THEME)} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={isActive('/coach/recommendations')}
              onClick={() => navigate('/coach/recommendations')}
              sx={itemSx('/coach/recommendations', PLAYER_THEME)}
            >
              <ListItemIcon sx={{ color: iconColor('/coach/recommendations', PLAYER_THEME), minWidth: 56 }}>
                <RecommendationsIcon />
              </ListItemIcon>
              <ListItemText primary="AI Assistant" primaryTypographyProps={textProps('/coach/recommendations', PLAYER_THEME)} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={isActive('/coach/certificates')}
              onClick={() => navigate('/coach/certificates')}
              sx={itemSx('/coach/certificates', PLAYER_THEME)}
            >
              <ListItemIcon sx={{ color: iconColor('/coach/certificates', PLAYER_THEME), minWidth: 56 }}>
                <CertificateIcon />
              </ListItemIcon>
              <ListItemText primary="Certificates" primaryTypographyProps={textProps('/coach/certificates', PLAYER_THEME)} />
            </ListItemButton>
          </ListItem>
        </Box>
      </List>

      <SidebarAccountFooter
        variant="dark"
        onLogout={handleLogout}
        links={{
          profile: '/coach/profile',
          subscriptions: '/coach?tab=subscription',
          invoices: '/coach/invoices',
        }}
      />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: 'none',
          },
        }}
      >
        {drawer}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          overflow: 'auto',
        }}
      >
        {children || <Outlet />}
      </Box>
    </Box>
  );
};

export default CoachLayout;
